import Header from "./Header";
import Footer from "./Footer";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";

// ─── Reusable animation variants ───────────────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut", delay: i * 0.1 },
  }),
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: (i = 0) => ({
    opacity: 1,
    transition: { duration: 0.7, ease: "easeOut", delay: i * 0.08 },
  }),
};

const slideLeft = {
  hidden: { opacity: 0, x: -50 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const slideRight = {
  hidden: { opacity: 0, x: 50 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.85 },
  visible: (i = 0) => ({
    opacity: 1,
    scale: 1,
    transition: { duration: 0.55, ease: "easeOut", delay: i * 0.1 },
  }),
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.12, delayChildren: 0.1 },
  },
};

// ─── Hook: trigger animation once when element enters viewport ─────────────
function useScrollReveal(threshold = 0.15) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: threshold });
  return [ref, isInView];
}

function SLNEye() {
  // ── Section refs ──────────────────────────────────────────────────────────
  const [heroRef, heroInView] = useScrollReveal(0.1);
  const [introRef, introInView] = useScrollReveal(0.15);
  const [purposeRef, purposeInView] = useScrollReveal(0.15);
  const [useCasesRef, useCasesInView] = useScrollReveal(0.15);
  const [whatIsRef, whatIsInView] = useScrollReveal(0.15);
  const [madeForRef, madeForInView] = useScrollReveal(0.2);
  const [ctaRef, ctaInView] = useScrollReveal(0.2);
  const [successRef, successInView] = useScrollReveal(0.15);
  const [complianceRef, complianceInView] = useScrollReveal(0.15);

  return (
    <>
      <Header />

      {/* ── Hero Section ──────────────────────────────────────────────────── */}
      <section className="bg-slneye p-8" ref={heroRef}>
        <div className="container py-1">
          <div className="row justify-content-center">
            <div className="col-lg-6 col-12 d-flex align-items-center">
              <div className="text-center">
                <motion.span
                  className="text-white d-block text-uppercase fw-semibold fs-4 ls-xl"
                  initial={{ opacity: 0, y: -20 }}
                  animate={heroInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                >
                  INTRODUCING
                </motion.span>

                <motion.p
                  className="m-0 display-1 mt-3 lh-1 science-gothic fw-bold ls-sm text-white"
                  initial={{ opacity: 0, scale: 0.8, y: 30 }}
                  animate={heroInView ? { opacity: 1, scale: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, ease: "easeOut", delay: 0.15 }}
                >
                  SLN SCAN
                </motion.p>

                <motion.h2
                  className="mb-4 display-5 text-white fw-medium lh-1"
                  initial={{ opacity: 0, y: 20 }}
                  animate={heroInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.7, ease: "easeOut", delay: 0.3 }}
                >
                  from Swinglearn Network LLP
                </motion.h2>

                <motion.p
                  className="lead text-white lh-sm"
                  initial={{ opacity: 0, y: 20 }}
                  animate={heroInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, ease: "easeOut", delay: 0.45 }}
                >
                  An advanced stock scanner that decodes institutional behavior
                  & reveals high-probability trades.
                </motion.p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Intro Section ─────────────────────────────────────────────────── */}
      <section className="bg-white p-lg-8 pt-lg-6 p-0 pb-6" ref={introRef}>
        <div className="container">
          <motion.div
            className="text-center"
            variants={fadeUp}
            initial="hidden"
            animate={introInView ? "visible" : "hidden"}
          >
            <h5 className="lh-sm mb-2 fs-3 fw-normal text-dark">
              The scanner is designed purely for learning and practice purposes,
              enabling students to understand how professional
              <br />
              traders and analysts read market data.
            </h5>
            <h2 className="h1 text-dark fw-normal ls-sm lh-1">
              The Scanner That Thinks — and Sees{" "}
              <span className="fw-bold">Like a Pro Trader</span>
            </h2>
            <div className="mt-n3 pt-2">
              <img
                src="/assets/images/line-shap.webp"
                width="230"
                className="mt-2"
              />
            </div>
          </motion.div>

          <div className="row mt-6">
            <motion.div
              className="col-lg-7 pe-8"
              variants={slideLeft}
              initial="hidden"
              animate={introInView ? "visible" : "hidden"}
            >
              <p className="lead fw-medium text-dark opacity-75 lh-sm">
                SLN Scan is an advanced educational stock market scanner
                developed by SLN Academy (Swinglearn Network LLP) to help
                students learn real-time market analysis using live market data.
              </p>
              <p className="lead fw-medium text-dark opacity-75 lh-sm mt-4">
                Data Coverage (Educational Display Only)
              </p>
              <p className="lead fw-medium text-dark lh-sm">
                SLN Scan will display real-time and near real-time market data
                sourced through an authorized broking partner's market data API,
                including:
              </p>
              <motion.ul
                className="lead fw-medium text-dark lh-sm mt-3"
                variants={staggerContainer}
                initial="hidden"
                animate={introInView ? "visible" : "hidden"}
              >
                {[
                  "Option Chain Data (NFO Segment)",
                  "Live Market Depth (Bid / Ask)",
                  "Open Interest (OI) & Change in OI",
                  "Volume & Delivery Data",
                ].map((item, i) => (
                  <motion.li key={i} variants={fadeUp} custom={i}>
                    {item}
                  </motion.li>
                ))}
                <motion.li variants={fadeUp} custom={4}>
                  Price Action / Candle Feed
                  <ul>
                    {["1 Minute", "5 Minutes", "15 Minutes"].map((t, i) => (
                      <motion.li key={i} variants={fadeUp} custom={5 + i}>
                        {t}
                      </motion.li>
                    ))}
                  </ul>
                </motion.li>
                {[
                  "Top Gainers & Losers",
                  "Sectoral Indices & Derivatives Data",
                  "Historical Market Data (optional – for backtesting & learning)",
                ].map((item, i) => (
                  <motion.li key={i + 5} variants={fadeUp} custom={8 + i}>
                    {item}
                  </motion.li>
                ))}
              </motion.ul>
            </motion.div>

            <motion.div
              className="col-lg-5"
              variants={slideRight}
              initial="hidden"
              animate={introInView ? "visible" : "hidden"}
              whileHover={{ scale: 1.015 }}
            >
              <iframe
                className="w-100 object-fit-cover rounded-5 mt-lg-0 mt-4"
                height="315"
                src="https://www.youtube.com/embed/CKN1h-aqVf8?si=bHqj4D5zuhYI0wGs"
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              ></iframe>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Purpose Section ───────────────────────────────────────────────── */}
      <section className="bg-black py-6" ref={purposeRef}>
        <div className="container pt-3">
          <motion.div
            className="text-center"
            variants={fadeUp}
            initial="hidden"
            animate={purposeInView ? "visible" : "hidden"}
          >
            <h5 className="mb-0 display-5 fw-normal text-light">
              Purpose of <span className="text-gold fw-semibold">SLN Scan</span>
            </h5>
            <motion.h3
              className="text-white fw-normal ls-sm lh-sm mt-3"
              variants={fadeUp}
              initial="hidden"
              animate={purposeInView ? "visible" : "hidden"}
              custom={1}
            >
              Designed with deep insights from the SLN Strategy, this scanner
              filters out market noise and shows you only what matters — stocks
              that align with smart money behaviour and institutional patterns.
            </motion.h3>
            <motion.h2
              className="text-white fw-normal ls-sm lh-1 mt-3"
              variants={fadeUp}
              initial="hidden"
              animate={purposeInView ? "visible" : "hidden"}
              custom={2}
            >
              The objective of SLN Scan is to enhance practical learning by
              allowing students to:
            </motion.h2>

            <motion.div
              className="d-flex justify-content-center flex-wrap align-items-center gap-1 mt-6"
              variants={staggerContainer}
              initial="hidden"
              animate={purposeInView ? "visible" : "hidden"}
            >
              {[
                {
                  bg: "bg-light-warning",
                  icon: "bi-bar-chart",
                  label: "Learn live stock\nselection logic",
                },
                {
                  bg: "bg-light-info",
                  icon: "bi-save",
                  label: "Understand option\nchain interpretation",
                },
                {
                  bg: "bg-light-danger",
                  icon: "bi-x-lg",
                  label: "Practice data-driven\nmarket analysis",
                },
                {
                  bg: "bg-light-dark",
                  icon: "bi-eye",
                  label:
                    "Improve decision-making skills in a\nsimulated learning environment",
                },
              ].map(({ bg, icon, label }, i) => (
                <motion.div
                  key={i}
                  className="d-flex align-items-center bg-light p-1 pe-5 rounded-pill"
                  variants={scaleIn}
                  custom={i}
                  whileHover={{
                    scale: 1.06,
                    boxShadow: "0 6px 24px rgba(255,255,255,0.12)",
                    y: -4,
                  }}
                  transition={{ type: "spring", stiffness: 250 }}
                >
                  <div className={`icon-shape icon-xl ${bg} rounded-circle`}>
                    <i className={`bi ${icon} fs-3 text-primary`}></i>
                  </div>
                  <div className="ms-2 me-3 text-start">
                    <p className="text-secondary lh-1 m-0 fs-4">
                      {label.split("\n").map((line, j) => (
                        <span key={j}>
                          {line}
                          {j < label.split("\n").length - 1 && <br />}
                        </span>
                      ))}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            <motion.p
              className="lead fw-normal text-danger lh-sm mt-5"
              variants={fadeUp}
              initial="hidden"
              animate={purposeInView ? "visible" : "hidden"}
              custom={4}
            >
              SLN Scan does not generate buy/sell signals, tips, or
              recommendations.
              <br />
              All data is shown only for educational and analytical learning.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ── Key Use Cases ─────────────────────────────────────────────────── */}
      <section className="bg-black pb-5" ref={useCasesRef}>
        <div className="container">
          <motion.div
            className="text-center"
            variants={fadeUp}
            initial="hidden"
            animate={useCasesInView ? "visible" : "hidden"}
          >
            <h2 className="h1 text-white fw-normal ls-sm lh-1">
              Key Learning Use-Cases
            </h2>
            <div className="mt-n3 pt-2">
              <img
                src="/assets/images/line-shap.webp"
                width="230"
                className="mt-2"
              />
            </div>
          </motion.div>

          <div className="row mt-6">
            <motion.div
              className="col-lg-7 pe-8"
              variants={slideLeft}
              initial="hidden"
              animate={useCasesInView ? "visible" : "hidden"}
            >
              <p className="lead fw-medium text-light opacity-75 lh-sm">
                SLN Scan isn't just another stock scanner — it's a smart trading
                system built on the trusted SLN Framework, designed to detect
                institutional behaviour long before the market reacts.
              </p>

              <motion.ul
                className="lead fw-medium text-light lh-sm mt-4"
                variants={staggerContainer}
                initial="hidden"
                animate={useCasesInView ? "visible" : "hidden"}
              >
                {[
                  "Understanding market structure & liquidity",
                  "Reading OI buildup, unwinding & volume spikes",
                  "Learning price action with real-time candles",
                  "Sector-wise movement analysis",
                  "Scanner-based stock shortlisting (learning-only)",
                ].map((item, i) => (
                  <motion.li
                    key={i}
                    variants={fadeUp}
                    custom={i}
                    whileHover={{ x: 6 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    {item}
                  </motion.li>
                ))}
              </motion.ul>
            </motion.div>

            <motion.div
              className="col-lg-5"
              variants={slideRight}
              initial="hidden"
              animate={useCasesInView ? "visible" : "hidden"}
              whileHover={{ scale: 1.03 }}
              transition={{ type: "spring", stiffness: 180 }}
            >
              <img
                src="/assets/images/stock-graph.jpg"
                className="w-100 object-fit-cover rounded-5 my-lg-0 my-6"
                height="300"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── What is SLN Scan ──────────────────────────────────────────────── */}
      <section className="bg-black pb-8 pt-6" ref={whatIsRef}>
        <div className="container">
          <div className="row pt-6">
            <motion.div
              className="col-lg-10 pe-8"
              variants={fadeUp}
              initial="hidden"
              animate={whatIsInView ? "visible" : "hidden"}
            >
              <h2 className="h1 text-white fw-normal ls-sm lh-1">
                What is SLN Scan?
              </h2>
              <p className="lead fw-medium text-light opacity-75 lh-sm">
                SLN Scan is not just another scanner — it's your edge in the
                market, designed for traders who follow the Demand-Supply
                Concept. It filters stocks using real-time data, technical
                levels, and unique SLN metrics to help you spot institutional
                activity, breakout opportunities, and high-reward opportunities
                — fast.
              </p>

              <motion.ul
                className="lead fw-medium text-light lh-sm"
                variants={staggerContainer}
                initial="hidden"
                animate={whatIsInView ? "visible" : "hidden"}
              >
                {[
                  "Built on the proven SLN Framework",
                  "Easy-to-use filters with advanced backend logic",
                  "Updated daily with fresh setups",
                  "Perfect for intraday, swing, and positional traders",
                ].map((item, i) => (
                  <motion.li
                    key={i}
                    variants={fadeUp}
                    custom={i}
                    whileHover={{ x: 6 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    {item}
                  </motion.li>
                ))}
              </motion.ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Made for Every Trader ─────────────────────────────────────────── */}
      <section className="bg-black pb-8" ref={madeForRef}>
        <div className="container">
          <motion.div
            className="text-center"
            variants={fadeUp}
            initial="hidden"
            animate={madeForInView ? "visible" : "hidden"}
          >
            <h2 className="display-2 text-white fw-normal ls-sm lh-1 mb-3">
              Made for Every Serious Trader
            </h2>
            <motion.span
              className="text-white d-block text-uppercase fw-semibold fs-4 ls-xl"
              variants={fadeUp}
              initial="hidden"
              animate={madeForInView ? "visible" : "hidden"}
              custom={1}
            >
              Whether you're just starting out or have years of experience, GTF
              Scan gives you clarity when it matters most.
            </motion.span>
          </motion.div>
        </div>
      </section>

      {/* ── CTA Section ───────────────────────────────────────────────────── */}
      <section className="pb-8 pt-2" ref={ctaRef}>
        <div className="container">
          <motion.div
            className="text-center"
            variants={fadeUp}
            initial="hidden"
            animate={ctaInView ? "visible" : "hidden"}
          >
            <motion.img
              src="/assets/images/t-image.png"
              className="my-3 mb-n4"
              width="380"
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              animate={ctaInView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ duration: 0.7, ease: "easeOut" }}
              whileHover={{ scale: 1.04, y: -5 }}
            />

            <motion.h2
              className="display-4 fw-semibold ls-sm lh-1"
              variants={fadeUp}
              initial="hidden"
              animate={ctaInView ? "visible" : "hidden"}
              custom={1}
            >
              Your Edge, Just One Click Away, SLN Scan is Not a Tool.
              <br />
              It's a New Way to Trade.
            </motion.h2>

            <motion.h3
              className="mb-0 fw-normal text-dark opacity-75"
              variants={fadeUp}
              initial="hidden"
              animate={ctaInView ? "visible" : "hidden"}
              custom={2}
            >
              Why keep struggling when you can have a system that's built to
              think like a professional?
            </motion.h3>

            <motion.div
              className="mt-4"
              variants={staggerContainer}
              initial="hidden"
              animate={ctaInView ? "visible" : "hidden"}
            >
              <motion.div
                variants={fadeUp}
                custom={0}
                whileHover={{ scale: 1.06 }}
                whileTap={{ scale: 0.96 }}
                style={{ display: "inline-block" }}
              >
                <Link
                  to="/about"
                  className="btn bg-gold text-black fw-normal fs-3 w-auto mt-2 px-6 me-lg-2 rounded-pill"
                >
                  Explore SLN Academy
                </Link>
              </motion.div>
              <motion.div
                variants={fadeUp}
                custom={1}
                whileHover={{ scale: 1.06 }}
                whileTap={{ scale: 0.96 }}
                style={{ display: "inline-block" }}
              >
                <a
                  href="https://www.youtube.com/@SLNAcademyOfficial"
                  target="_blank"
                  className="btn btn-dark fw-normal fs-3 w-auto mt-2 px-6 me-lg-0 rounded-pill"
                >
                  Subscribe Now
                </a>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── Success Story / Videos ────────────────────────────────────────── */}
      <section ref={successRef}>
        <div className="container mb-7">
          <motion.div
            className="row"
            variants={fadeUp}
            initial="hidden"
            animate={successInView ? "visible" : "hidden"}
          >
            <div className="row text-center">
              <div className="col-md-12 px-lg-8">
                <span className="text-uppercase text-secondary fw-semibold ls-md">
                  Alone we are smart. Together we are brilliant
                </span>
                <h2 className="h1 fw-semibold ls-sm text-success lh-1 mt-1">
                  Our Success Story
                </h2>
                <div className="mt-n4 pt-2">
                  <img
                    src="/assets/images/line-shap.webp"
                    width="230"
                    className="mt-2"
                  />
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="mt-5"
            variants={staggerContainer}
            initial="hidden"
            animate={successInView ? "visible" : "hidden"}
          >
            <div className="row justify-content-center align-items-center g-2">
              <motion.div
                className="col-3 d-lg-block d-none"
                variants={fadeUp}
                custom={0}
                whileHover={{ scale: 1.015 }}
              >
                <iframe
                  height="220"
                  src="https://www.youtube.com/embed/kyumSEho45Q?si=azx4y3HHSq8YZVbf"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  className="w-100 object-fit-cover rounded-5"
                ></iframe>
              </motion.div>
              <motion.div
                className="col-lg-6 col-12"
                variants={scaleIn}
                custom={1}
                whileHover={{ scale: 1.01 }}
              >
                <iframe
                  height="300"
                  src="https://www.youtube.com/embed/tMQtBBNlMTg?si=ZqXNcuYD0NxTrJdM"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  className="w-100 object-fit-cover rounded-5"
                ></iframe>
              </motion.div>
              <motion.div
                className="col-3 d-lg-block d-none"
                variants={fadeUp}
                custom={2}
                whileHover={{ scale: 1.015 }}
              >
                <iframe
                  height="220"
                  src="https://www.youtube.com/embed/kyumSEho45Q?si=azx4y3HHSq8YZVbf"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  className="w-100 object-fit-cover rounded-5"
                ></iframe>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Compliance Section ────────────────────────────────────────────── */}
      <section className="mb-7" ref={complianceRef}>
        <div className="container mt-4">
          <div className="row">
            <motion.div
              className="col-md-12"
              variants={fadeUp}
              initial="hidden"
              animate={complianceInView ? "visible" : "hidden"}
            >
              <motion.span
                className="text-uppercase text-primary fw-semibold ls-sm fs-3 lh-1"
                initial={{ opacity: 0, x: -20 }}
                animate={complianceInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, ease: "easeOut" }}
              >
                Compliance & Usage Policy
              </motion.span>

              <motion.div
                variants={staggerContainer}
                initial="hidden"
                animate={complianceInView ? "visible" : "hidden"}
              >
                <motion.h3
                  className="fw-normal ls-sm text-dark lh-1 mt-1 m-0 mb-3"
                  variants={fadeUp}
                  custom={1}
                >
                  SLN Scan will strictly follow all regulatory and compliance
                  requirements:
                  <br />
                  <br />
                  <motion.ul
                    variants={staggerContainer}
                    initial="hidden"
                    animate={complianceInView ? "visible" : "hidden"}
                  >
                    {[
                      "Data usage limited to educational purposes only",
                      "Access restricted to registered students",
                      "No advisory, no execution, no trade calls",
                      "Mandatory disclaimer displayed on every scanner page",
                    ].map((item, i) => (
                      <motion.li
                        key={i}
                        variants={fadeUp}
                        custom={i}
                        whileHover={{ x: 5 }}
                        transition={{ type: "spring", stiffness: 300 }}
                      >
                        {item}
                      </motion.li>
                    ))}
                  </motion.ul>
                  <motion.span
                    variants={fadeUp}
                    custom={5}
                    style={{ display: "block" }}
                  >
                    Branding & Attribution
                  </motion.span>
                  <br />
                  <motion.ul
                    variants={staggerContainer}
                    initial="hidden"
                    animate={complianceInView ? "visible" : "hidden"}
                  >
                    {[
                      '"Powered by Choice Broking" (or API partner) will be clearly displayed',
                      "All API usage terms and branding guidelines will be followed",
                    ].map((item, i) => (
                      <motion.li
                        key={i}
                        variants={fadeUp}
                        custom={6 + i}
                        whileHover={{ x: 5 }}
                        transition={{ type: "spring", stiffness: 300 }}
                      >
                        {item}
                      </motion.li>
                    ))}
                  </motion.ul>
                  <br />
                  <motion.span
                    className="text-danger"
                    variants={fadeUp}
                    custom={9}
                    style={{ display: "block" }}
                  >
                    Mandatory Disclaimer
                  </motion.span>
                  <br />
                  <motion.span
                    variants={fadeUp}
                    custom={10}
                    style={{ display: "block" }}
                  >
                    SLN Scan is an educational tool developed by SLN Academy.{" "}
                    <br />
                    All market data displayed is for learning and analysis
                    purposes only. <br />
                    SLN Academy does not provide investment advice, trading
                    tips, or buy/sell recommendations. <br />
                    Trading and investment decisions are solely the
                    responsibility of the user. <br />
                    Market investments are subject to risk.
                  </motion.span>
                </motion.h3>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}

export default SLNEye;
